import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronsUpDown, Check, Plus } from "lucide-react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { setActiveOrgSlug } from "@/lib/api";
import type { Me } from "@/lib/types";
import { cn } from "@/lib/cn";

export function OrgSwitcher({ me }: { me: Me | null }) {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  if (!me || me.orgs.length === 0) return null;

  const stored =
    typeof window !== "undefined" ? localStorage.getItem("activeOrgSlug") : null;
  const currentSlug =
    (stored && me.orgs.find((o) => o.orgSlug === stored)?.orgSlug) ??
    me.orgs[0]?.orgSlug ??
    null;
  const current = me.orgs.find((o) => o.orgSlug === currentSlug);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-sm font-medium hover:bg-muted">
          <span className="truncate max-w-[140px]">
            {current?.orgName ?? "—"}
          </span>
          <ChevronsUpDown size={14} className="text-muted-foreground" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[240px] rounded-md border border-border bg-card shadow-lg p-1"
          sideOffset={6}
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs uppercase tracking-wider text-muted-foreground font-display">
            Your orgs
          </DropdownMenu.Label>
          {me.orgs.map((o) => (
            <DropdownMenu.Item
              key={o.orgId}
              onSelect={() => {
                if (!o.orgSlug) return;
                localStorage.setItem("activeOrgSlug", o.orgSlug);
                setActiveOrgSlug(o.orgSlug);
                qc.invalidateQueries();
              }}
              className={cn(
                "flex items-center justify-between gap-2 rounded px-2 py-2 text-sm cursor-pointer outline-none",
                "data-[highlighted]:bg-muted",
              )}
            >
              <span className="flex flex-col">
                <span className="font-medium">{o.orgName}</span>
                <span className="text-xs text-muted-foreground">
                  {o.orgSlug} · {o.role}
                </span>
              </span>
              {o.orgSlug === currentSlug ? <Check size={14} /> : null}
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={() => setLocation("/dashboard/orgs/new")}
            className="flex items-center gap-2 rounded px-2 py-2 text-sm cursor-pointer outline-none data-[highlighted]:bg-muted"
          >
            <Plus size={14} />
            Create new org
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
